import { createContext, useCallback, useEffect, useState } from 'react';
import { startOfWeek, endOfWeek, addWeeks, subWeeks, isSameWeek, isThisWeek, addDays } from 'date-fns';

const URI = process.env.REACT_APP_API_URL || 'http://localhost:8080';

// Webhook de n8n que, dado un deal id (o lead id) de HubSpot, resuelve el deal +
// su contacto y devuelve { firstName, lastName, email, phone, description, seller, dealId }.
const DEAL_LOOKUP_API =
  'https://n8n.n8nmanguito.lat/webhook/hubspot-deal-lookup';

export const CalendarContext = createContext();

// El link de HubSpot puede llegar como `?id=<deal_id>` (deal) o `?leadId=<lead_id>`
// (lead). Se leen una sola vez al cargar. Para un lead, n8n resuelve el deal más
// reciente asociado y de ahí en adelante el flujo es idéntico al de un deal.
// La lectura es case-insensitive: aceptamos `id`/`ID`, `leadId`/`leadid`/`LEADID`,
// etc., porque el link puede generarse con distinta capitalización.
const lookupParams = new URLSearchParams(window.location.search);
const getParamCI = (name) => {
  const target = name.toLowerCase();
  for (const [key, value] of lookupParams.entries()) {
    if (key.toLowerCase() === target) return value;
  }
  return null;
};
const hubspotDealId = getParamCI('id');
const hubspotLeadId = getParamCI('leadId');

export const CalendarProvider = ({ children }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [weekDay, setWeekDay] = useState(new Date());
  const [availableHours, setAvailableHours] = useState([]);
  const [dealData, setDealData] = useState(null);

  const listAvailableTimes = useCallback(async () => {
    const today = new Date();
    try {
      const isCurrentWeek = isThisWeek(currentDate, { weekStartsOn: 1 });

      const requestBody = {
        timeMin: isCurrentWeek ? today.toISOString() : startOfWeek(currentDate, { weekStartsOn: 1 }).toISOString(),
        timeMax: endOfWeek(currentDate, { weekStartsOn: 1 }).toISOString(),
      };

      const response = await fetch(`${URI}api/calendar/available-times`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setAvailableHours(data);
      return data;
    } catch (error) {
      console.error("Could not fetch available times: ", error);
    }
  }, [currentDate]);

  useEffect(() => {
    listAvailableTimes();
  }, [listAvailableTimes]);

  // Si el link trae un deal id o un lead id de HubSpot, traemos los datos del deal
  // (vía n8n) una sola vez para precargar el formulario de reserva. Si falla o no
  // hay id, `dealData` queda null y el formulario funciona vacío como siempre.
  useEffect(() => {
    if (!hubspotDealId && !hubspotLeadId) return;
    const fetchDealData = async () => {
      try {
        const lookupQuery = hubspotLeadId
          ? `leadId=${encodeURIComponent(hubspotLeadId)}`
          : `id=${encodeURIComponent(hubspotDealId)}`;
        const response = await fetch(`${DEAL_LOOKUP_API}?${lookupQuery}`);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        setDealData(data);
      } catch (error) {
        console.error('Could not fetch HubSpot deal data: ', error);
      }
    };
    fetchDealData();
  }, []);

  const goToPreviousWeek = () => {
    const today = new Date();
    const prevWeek = subWeeks(currentDate, 1);
    const prevWeekToday = subWeeks(today, 1);
    if (!isSameWeek(prevWeek, prevWeekToday, { weekStartsOn: 1 })) {
      setCurrentDate(prevWeek);
      setWeekDay(startOfWeek(prevWeek, { weekStartsOn: 1 }));
    }
  };

  const goToNextWeek = () => {
    const nextWeek = addWeeks(currentDate, 1);
    setCurrentDate(nextWeek);
    setWeekDay(startOfWeek(nextWeek, { weekStartsOn: 1 }));
  };

  const goToCurrentWeek = () => {
    const today = new Date();
    if (!isSameWeek(startOfWeek(currentDate, { weekStartsOn: 1 }), startOfWeek(today, { weekStartsOn: 1 }), { weekStartsOn: 1 })) {
      setCurrentDate(today);
      setWeekDay(startOfWeek(today, { weekStartsOn: 1 }));
    }
  };

  // Id del deal sobre el que se hace el write-back al agendar. Para un link de deal
  // es el de la URL directo (preserva el comportamiento aunque el lookup falle);
  // para un link de lead es el deal resuelto por n8n (viene en `dealData.dealId`).
  const resolvedDealId = hubspotDealId || dealData?.dealId || null;

  const contextValue = {
    currentDate,
    weekDay,
    setWeekDay,
    setCurrentDate,
    isModalOpen,
    setIsModalOpen,
    goToPreviousWeek,
    goToNextWeek,
    goToCurrentWeek,
    startOfWeek: startOfWeek(currentDate, { weekStartsOn: 1 }),
    endOfWeek: endOfWeek(currentDate, { weekStartsOn: 1 }),
    availableHours,
    listAvailableTimes,
    hubspotDealId,
    hubspotLeadId,
    resolvedDealId,
    dealData
  };

  return (
    <CalendarContext.Provider value={contextValue}>
      {children}
    </CalendarContext.Provider>
  );
};
